import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { useCallback, useState } from 'react';
import Iconify from './Iconify';
import {
    articleMediaCloseButtonSx,
    articleMediaDialogContentSx,
} from './articleFolder/articleMediaDialogStyles';
import {
    downloadPdfBlob,
    fetchUrlAsPdfBlob,
    printPdf,
} from '../utils/pdfPreviewUtils';
import '../css/openPDFDialog.css';

const PDF_WORKER_URL = 'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';

export const PdfViewer = ({ fileUrl }) => (
    <Box className="OpenPDFDialog_viewer">
        <Worker workerUrl={PDF_WORKER_URL}>
            <Viewer fileUrl={fileUrl} theme="light" />
        </Worker>
    </Box>
);

const OpenPDFDialog = ({
    open,
    onClose,
    fileUrl,
    fileName = 'document.pdf',
    title = 'Document PDF',
    loading = false,
    getBlob,
    articleView = false,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [actionLoading, setActionLoading] = useState(null);

    const resolveBlob = useCallback(async () => {
        const cachedBlob = getBlob?.();
        if (cachedBlob) {
            return cachedBlob;
        }
        if (fileUrl) {
            return fetchUrlAsPdfBlob(fileUrl);
        }
        throw new Error('Aucun document disponible');
    }, [getBlob, fileUrl]);

    const handleDownload = async () => {
        try {
            setActionLoading('download');
            const blob = await resolveBlob();
            downloadPdfBlob(blob, fileName);
        } catch (error) {
            console.error('Erreur lors du téléchargement du PDF:', error);
        } finally {
            setActionLoading(null);
        }
    };

    const handlePrint = async () => {
        try {
            setActionLoading('print');
            const blob = await resolveBlob();
            await printPdf({ blob });
        } catch (error) {
            console.error('Erreur lors de l\'impression du PDF:', error);
        } finally {
            setActionLoading(null);
        }
    };

    const handleClose = () => {
        setActionLoading(null);
        onClose();
    };

    const showViewer = open && fileUrl && !loading;

    if (articleView) {
        return (
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="lg"
                fullWidth
                scroll="paper"
                classes={{ paper: 'OpenPDFDialog_paper OpenPDFDialog_paper_article' }}
            >
                <DialogContent
                    sx={{
                        ...articleMediaDialogContentSx,
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                        p: { xs: 1, sm: 2 },
                    }}
                >
                    <IconButton
                        onClick={handleClose}
                        sx={articleMediaCloseButtonSx}
                        aria-label="Fermer"
                    >
                        <Iconify icon="material-symbols:close" />
                    </IconButton>

                    {loading && (
                        <Box className="OpenPDFDialog_loading" sx={{ flex: 1 }}>
                            <CircularProgress
                                sx={{ '& .MuiCircularProgress-circle': { stroke: '#EA5E00' } }}
                            />
                            <Typography className="OpenPDFDialog_loading_text">
                                Chargement du document…
                            </Typography>
                        </Box>
                    )}

                    {showViewer && (
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <PdfViewer fileUrl={fileUrl} />
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullScreen={isMobile}
            fullWidth
            maxWidth="lg"
            scroll="paper"
            classes={{ paper: 'OpenPDFDialog_paper' }}
        >
            <Box className="OpenPDFDialog_header">
                <Typography className="OpenPDFDialog_title" title={title}>
                    {title}
                </Typography>
                <IconButton
                    onClick={handleClose}
                    aria-label="Fermer"
                    size="small"
                >
                    <Iconify className="OpenPDFDialog_close_icon" icon="ic:round-close" />
                </IconButton>
            </Box>

            <Box className="OpenPDFDialog_body">
                {loading && (
                    <Box className="OpenPDFDialog_loading">
                        <CircularProgress
                            sx={{ '& .MuiCircularProgress-circle': { stroke: '#EA5E00' } }}
                        />
                        <Typography className="OpenPDFDialog_loading_text">
                            Génération du document en cours…
                        </Typography>
                    </Box>
                )}

                {showViewer && <PdfViewer fileUrl={fileUrl} />}
            </Box>

            <Box className="OpenPDFDialog_actions">
                <Button
                    className="OpenPDFDialog_action_button OpenPDFDialog_action_button_secondary"
                    variant="outlined"
                    onClick={handleClose}
                    disabled={Boolean(actionLoading)}
                >
                    Fermer
                </Button>
                <Button
                    className="OpenPDFDialog_action_button OpenPDFDialog_action_button_primary"
                    variant="contained"
                    onClick={handleDownload}
                    disabled={loading || !fileUrl || Boolean(actionLoading)}
                    startIcon={
                        actionLoading === 'download'
                            ? <CircularProgress size={18} color="inherit" />
                            : <Iconify icon="material-symbols:download-rounded" />
                    }
                >
                    Télécharger
                </Button>
                <Button
                    className="OpenPDFDialog_action_button OpenPDFDialog_action_button_secondary"
                    variant="outlined"
                    onClick={handlePrint}
                    disabled={loading || !fileUrl || Boolean(actionLoading)}
                    startIcon={
                        actionLoading === 'print'
                            ? <CircularProgress size={18} color="inherit" />
                            : <Iconify icon="material-symbols:print-outline" />
                    }
                >
                    Imprimer
                </Button>
            </Box>
        </Dialog>
    );
};

export default OpenPDFDialog;
